import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import {
  ExclamationTriangleIcon,
  FireIcon,
  EyeIcon,
  CheckCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

import Layout from '../components/Layout';
import { opportunitiesApi, feedbackApi } from '../utils/api';
import type { OpportunityMatch } from '../types';

interface DashboardStats {
  today_matches: number;
  week_matches: number;
  month_stats: {
    pursued: number;
    clicked: number;
    ignored: number;
  };
  quality_metrics: {
    click_through_rate: number;
    pursuit_rate: number;
    false_positive_rate: number;
  };
}

const Dashboard: React.FC = () => {
  const [matches, setMatches] = useState<OpportunityMatch[]>([]);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'high' | 'moderate'>('all');

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      const [matchesResponse, statsResponse] = await Promise.all([
        opportunitiesApi.getMatches({ days: 7 }),
        opportunitiesApi.getStats(),
      ]);

      setMatches(matchesResponse.data.matches);
      setStats(statsResponse.data);
    } catch (error: any) {
      toast.error('Failed to load dashboard data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleFeedback = async (matchId: number, action: string, reason?: string) => {
    try {
      await feedbackApi.createFeedback({
        match_id: matchId,
        action_type: action,
        ignore_reason: reason,
      });

      toast.success(`Marked as ${action}`);
      loadDashboardData(); // Refresh data
    } catch (error: any) {
      toast.error('Failed to submit feedback');
    }
  };

  const filteredMatches = matches.filter((match) => {
    if (filter === 'all') return true;
    return match.relevance_tier === filter;
  });

  const formatCurrency = (amount: number | undefined) => {
    if (!amount) return 'TBD';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="mt-1 text-sm text-gray-500">
            Your personalized government contract opportunities
          </p>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <FireIcon className="h-6 w-6 text-red-400" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        Today's Matches
                      </dt>
                      <dd className="text-lg font-medium text-gray-900">
                        {stats.today_matches}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <EyeIcon className="h-6 w-6 text-blue-400" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        Click Rate
                      </dt>
                      <dd className="text-lg font-medium text-gray-900">
                        {stats.quality_metrics.click_through_rate}%
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <CheckCircleIcon className="h-6 w-6 text-green-400" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        Pursuit Rate
                      </dt>
                      <dd className="text-lg font-medium text-gray-900">
                        {stats.quality_metrics.pursuit_rate}%
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <ExclamationTriangleIcon className="h-6 w-6 text-yellow-400" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        False Positive Rate
                      </dt>
                      <dd className="text-lg font-medium text-gray-900">
                        {stats.quality_metrics.false_positive_rate}%
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white shadow rounded-lg p-4">
          <div className="flex space-x-4">
            <button
              onClick={() => setFilter('all')}
              className={`px-4 py-2 rounded-md text-sm font-medium ${
                filter === 'all'
                  ? 'bg-blue-100 text-blue-700'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              All ({matches.length})
            </button>
            <button
              onClick={() => setFilter('high')}
              className={`px-4 py-2 rounded-md text-sm font-medium ${
                filter === 'high'
                  ? 'bg-red-100 text-red-700'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              High Priority ({matches.filter((m) => m.relevance_tier === 'high').length})
            </button>
            <button
              onClick={() => setFilter('moderate')}
              className={`px-4 py-2 rounded-md text-sm font-medium ${
                filter === 'moderate'
                  ? 'bg-yellow-100 text-yellow-700'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Moderate ({matches.filter((m) => m.relevance_tier === 'moderate').length})
            </button>
          </div>
        </div>

        {/* Opportunities List */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
              Recent Opportunities
            </h3>

            {filteredMatches.length === 0 ? (
              <div className="text-center py-12">
                <FireIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">
                  No opportunities found
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  Check back later for new matches or adjust your profile settings.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredMatches.map((match) => (
                  <div
                    key={match.id}
                    className={`border rounded-lg p-4 ${
                      match.relevance_tier === 'high'
                        ? 'border-l-4 border-l-red-500 bg-red-50'
                        : 'border-l-4 border-l-yellow-500 bg-yellow-50'
                    }`}
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h4 className="text-lg font-medium text-gray-900">
                          {match.opportunity.title}
                        </h4>
                        <div className="mt-1 text-sm text-gray-600 space-y-1">
                          <p>
                            <strong>Agency:</strong> {match.opportunity.agency}
                          </p>
                          <p>
                            <strong>Value:</strong>{' '}
                            {formatCurrency(match.opportunity.contract_value_min)} -{' '}
                            {formatCurrency(match.opportunity.contract_value_max)}
                          </p>
                          <p>
                            <strong>Deadline:</strong>{' '}
                            {match.opportunity.response_deadline
                              ? format(new Date(match.opportunity.response_deadline), 'MM/dd/yyyy')
                              : 'TBD'}
                          </p>
                          <p>
                            <strong>NAICS:</strong> {match.opportunity.naics_code}
                          </p>
                        </div>
                        {match.reasoning && (
                          <div className="mt-2 p-2 bg-white rounded text-sm text-gray-700">
                            <strong>Why matched:</strong> {match.reasoning}
                          </div>
                        )}
                      </div>
                      <div className="ml-4">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            match.relevance_tier === 'high'
                              ? 'bg-red-100 text-red-800'
                              : 'bg-yellow-100 text-yellow-800'
                          }`}
                        >
                          {match.relevance_tier === 'high' ? '🎯 High Priority' : '📋 Moderate'}
                        </span>
                      </div>
                    </div>

                    <div className="mt-4 flex space-x-3">
                      <Link
                        to={`/opportunity/${match.id}`}
                        className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      >
                        View Details
                      </Link>
                      {match.opportunity.sam_url && (
                        <a
                          href={match.opportunity.sam_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                          View on SAM.gov
                        </a>
                      )}
                      <button
                        onClick={() => handleFeedback(match.id, 'pursued')}
                        className="inline-flex items-center px-3 py-2 border border-green-300 text-sm leading-4 font-medium rounded-md text-green-700 bg-white hover:bg-green-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                      >
                        <CheckCircleIcon className="w-4 h-4 mr-1" />
                        Mark as Pursuing
                      </button>
                      <button
                        onClick={() => handleFeedback(match.id, 'ignored')}
                        className="inline-flex items-center px-3 py-2 border border-red-300 text-sm leading-4 font-medium rounded-md text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                      >
                        <XMarkIcon className="w-4 h-4 mr-1" />
                        Not Relevant
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Dashboard;